import { useCallback, useEffect, useState } from 'react';
import { PencilSimple, Plus, Trash } from 'shared/icons';
import {
    ICMS_TYPE_DESCRIPTIONS,
    ICMS_TYPE_LABELS,
    ICMS_TYPES,
    PARAM_FIELDS,
    RULE_SCOPE_LABELS,
    RULE_SCOPES,
} from '../../constants/pricing';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { FormatInt } from '../../utils/format';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import ConfirmModal from '../ui/ConfirmModal';
import ErrorCard from '../ui/ErrorCard';
import Select from '../ui/Select';
import { SkeletonTable } from '../ui/Skeleton';
import StandardTable from '../ui/StandardTable';
import { useToast } from '../ui/Toast';
import { FormatDay, FormatFraction, IcmsTypePill, RuleFormModal, SkuLink } from './pricingShared';

/**
 * REGRAS — CRUD das regras de tratamento de ICMS. Precedência: SKU → NCM
 * (prefixo mais longo) → padrão; prioridade desempata; vigência permite o
 * mesmo SKU mudar de tipo entre lotes antigos e novos.
 */

const PARAM_LABEL = Object.fromEntries(PARAM_FIELDS.map((field) => [field.key, field]));

const ParamsSummary = ({ params }) => {
    const entries = Object.entries(params || {});
    if (entries.length === 0) {
        return <span className="text-xs text-mono-400">herda da empresa</span>;
    }
    return (
        <div className="flex flex-wrap gap-1">
            {entries.map(([key, value]) => {
                const field = PARAM_LABEL[key];
                const text =
                    field?.kind === 'fraction'
                        ? FormatFraction(value)
                        : field?.kind === 'flag'
                          ? value
                              ? 'sim'
                              : 'não'
                          : String(value);
                return (
                    <Badge key={key} variant="default" className="font-mono text-[11px]">
                        <span title={field?.help || key}>
                            {field?.label || key}: {text}
                        </span>
                    </Badge>
                );
            })}
        </div>
    );
};

const RulesPanel = () => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canWrite = checkPermission('pricing:write');
    const [rules, setRules] = useState([]);
    const [scopeFilter, setScopeFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const [modal, setModal] = useState({ open: false, initial: null });
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const refresh = useCallback(() => setRefreshTick((tick) => tick + 1), []);

    useEffect(() => {
        let cancelled = false;
        setError(null);
        api.getPricingRules({ scope_type: scopeFilter || undefined })
            .then((payload) => {
                if (!cancelled) {
                    setRules(Array.isArray(payload?.items) ? payload.items : []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar as regras.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setHasLoadedOnce(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [scopeFilter, refreshTick]);

    const remove = async () => {
        if (!confirmDelete) {
            return;
        }
        setDeleting(true);
        try {
            await api.deletePricingRule(confirmDelete.id);
            toast.success('Regra removida');
            setConfirmDelete(null);
            refresh();
        } catch (err) {
            toast.error(err?.message || 'Não foi possível remover');
        } finally {
            setDeleting(false);
        }
    };

    const visible = typeFilter ? rules.filter((rule) => rule.icms_type === typeFilter) : rules;

    const columns = [
        {
            key: 'scope',
            label: 'Escopo',
            getValue: (row) => `${RULE_SCOPE_LABELS[row.scope_type]} ${row.scope_value || ''}`,
            render: (row) => (
                <div className="space-y-0.5">
                    <Badge
                        variant={row.scope_type === 'sku' ? 'info' : row.scope_type === 'ncm' ? 'default' : 'neutral'}
                    >
                        {RULE_SCOPE_LABELS[row.scope_type]}
                    </Badge>
                    {row.scope_type === 'sku' ? (
                        <p>
                            <SkuLink sku={row.scope_value} />
                        </p>
                    ) : row.scope_value ? (
                        <p className="font-mono text-xs">{row.scope_value}</p>
                    ) : null}
                </div>
            ),
        },
        {
            key: 'name',
            label: 'Regra',
            getValue: (row) => row.name,
            render: (row) => (
                <div className="min-w-0">
                    <p className="text-sm font-medium text-black dark:text-white">{row.name}</p>
                    {row.note ? (
                        <p className="truncate text-xs text-mono-500" title={row.note}>
                            {row.note}
                        </p>
                    ) : null}
                </div>
            ),
        },
        {
            key: 'icms_type',
            label: 'Tratamento',
            getValue: (row) => row.icms_type,
            render: (row) => <IcmsTypePill type={row.icms_type} />,
        },
        {
            key: 'priority',
            label: 'Prior.',
            align: 'center',
            getValue: (row) => row.priority,
            render: (row) => <span className="font-mono text-xs">{FormatInt(row.priority)}</span>,
        },
        {
            key: 'validity',
            label: 'Vigência',
            getValue: (row) => `${row.valid_from || ''} ${row.valid_to || ''}`,
            render: (row) => (
                <span className="font-mono text-xs">
                    {row.valid_from ? FormatDay(row.valid_from) : 'sempre'} →{' '}
                    {row.valid_to ? FormatDay(row.valid_to) : 'aberta'}
                </span>
            ),
        },
        {
            key: 'active',
            label: 'Ativa',
            align: 'center',
            getValue: (row) => (row.active ? 1 : 0),
            render: (row) => <Badge variant={row.active ? 'success' : 'neutral'}>{row.active ? 'sim' : 'não'}</Badge>,
        },
        {
            key: 'params',
            label: 'Parâmetros',
            sortable: false,
            getValue: (row) => JSON.stringify(row.params || {}),
            render: (row) => <ParamsSummary params={row.params} />,
        },
        ...(canWrite
            ? [
                  {
                      key: 'actions',
                      label: '',
                      sortable: false,
                      getValue: () => '',
                      render: (row) => (
                          <div className="flex justify-end gap-1">
                              <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(event) => {
                                      event.stopPropagation();
                                      setModal({ open: true, initial: row });
                                  }}
                                  title="Editar"
                              >
                                  <PencilSimple size={16} />
                              </Button>
                              <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(event) => {
                                      event.stopPropagation();
                                      setConfirmDelete(row);
                                  }}
                                  title="Remover"
                              >
                                  <Trash size={16} />
                              </Button>
                          </div>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {ICMS_TYPES.map((type) => (
                    <div
                        key={type}
                        className="rounded-xl border border-mono-200 bg-white p-3 text-xs text-mono-600 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-300"
                    >
                        <p className="mb-1">
                            <IcmsTypePill type={type} />
                        </p>
                        {ICMS_TYPE_DESCRIPTIONS[type]}
                    </div>
                ))}
            </div>
            <div className="flex flex-wrap items-end gap-3">
                <div className="w-44">
                    <Select label="Escopo" value={scopeFilter} onChange={(event) => setScopeFilter(event.target.value)}>
                        <option value="">Todos</option>
                        {RULE_SCOPES.map((scope) => (
                            <option key={scope} value={scope}>
                                {RULE_SCOPE_LABELS[scope]}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="w-56">
                    <Select
                        label="Tratamento"
                        value={typeFilter}
                        onChange={(event) => setTypeFilter(event.target.value)}
                    >
                        <option value="">Todos</option>
                        {ICMS_TYPES.map((type) => (
                            <option key={type} value={type}>
                                {ICMS_TYPE_LABELS[type]}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="flex-1" />
                {canWrite ? (
                    <Button onClick={() => setModal({ open: true, initial: null })}>
                        <span className="inline-flex items-center gap-1.5">
                            <Plus size={16} /> Nova regra
                        </span>
                    </Button>
                ) : null}
            </div>
            {error ? (
                <ErrorCard
                    title="Erro ao carregar regras"
                    message={error}
                    action="Tentar novamente"
                    actionFn={refresh}
                />
            ) : !hasLoadedOnce ? (
                <SkeletonTable columns={7} rows={5} />
            ) : (
                <StandardTable
                    rows={visible}
                    columns={columns}
                    rowKey={(row) => row.id}
                    countLabel="regras"
                    emptyMessage="Nenhuma regra cadastrada — todo SKU usa o padrão da empresa até aqui."
                    exportFileName="regras-icms"
                    searchPlaceholder="Buscar por nome, SKU ou NCM..."
                    defaultPageSize={30}
                />
            )}
            <RuleFormModal
                isOpen={modal.open}
                initial={modal.initial}
                onClose={() => setModal({ open: false, initial: null })}
                onSaved={refresh}
            />
            <ConfirmModal
                isOpen={Boolean(confirmDelete)}
                onClose={() => setConfirmDelete(null)}
                onConfirm={remove}
                title="Remover regra"
                message={
                    confirmDelete
                        ? `Remover a regra "${confirmDelete.name}"? Os lotes voltam a cair na próxima regra (NCM, padrão ou empresa).`
                        : ''
                }
                confirmText="Remover"
                loading={deleting}
            />
        </div>
    );
};

export default RulesPanel;
